import { Component, Inject, OnInit, ViewChildren, QueryList, ElementRef } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, NgForm, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ArrayValidators } from 'src/app/core/validators';
import { QuizQuestion } from 'src/app/models';
import { AdminQuizQuestionDialogData } from './admin-quiz-question-dialog.viewmodel';

@Component({
  selector: 'app-admin-quiz-question-dialog',
  templateUrl: './admin-quiz-question-dialog.component.html',
  styleUrls: ['./admin-quiz-question-dialog.component.css']
})
export class AdminQuizQuestionDialogComponent implements OnInit {
  form!: FormGroup;
  title!: string;

  isNew = true;
  input = {
    _id:           '',
    question:      '',
    answers:       [] as string[],
    correctAnswer: '',
    hint:          '',
  };

  get question() { return this.form?.get('question') as FormControl; }
  get answers() { return this.form?.get('answers') as FormArray; }
  get correctAnswer() { return this.form?.get('correctAnswer') as FormControl; }
  get hint() { return this.form?.get('hint') as FormControl; }

  @ViewChildren('answerInput')
  answerInputs!: QueryList<ElementRef>;

  // Needed this to display option letters
  letterArray: string[] = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<AdminQuizQuestionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private data: AdminQuizQuestionDialogData) {
    this.validateCorrectAnswer = this.validateCorrectAnswer.bind(this);
  }

  ngOnInit() {
    this.load(this.data);
    this.initForm();
  }

  private load(data: AdminQuizQuestionDialogData) {
    // Dynamic title for Add or Edit Property
    this.title = data.title;

    if (data.edit) {
      this.isNew = false;
      this.input = {
        _id:           data.edit._id,
        question:      data.edit.question,
        answers:       data.edit.answers.slice(),
        correctAnswer: this.letterArray[data.edit.correctAnswerIndex] || '',
        hint:          data.edit.hint,
      };
    }
  }

  private initForm() {
    this.form = this.fb.group({
      question:      [this.input.question,      [Validators.required]],
      answers:       this.fb.array(
        this.input.answers,
        [
          Validators.required,
          ArrayValidators.minLength(2),
          ArrayValidators.maxLength(this.letterArray.length)
        ]
      ),
      correctAnswer: [this.input.correctAnswer, [Validators.required, this.validateCorrectAnswer]],
      hint:          [this.input.hint,          []],
    });

    if (this.isNew) {
      this.addAnswer(); // empty
    }
  }

  validate(form: FormGroup) {
    form.markAllAsTouched();
    form.updateValueAndValidity();
  }

  private validateCorrectAnswer = (control: AbstractControl) => {
    if (!this.answers) return null;

    const answer = control.value as string;
    if (!answer) return null;

    const answerIndex = this.indexOfLetter(answer);
    const answerCtrl = this.answers.controls[answerIndex];
    if (!answerCtrl) return { invalidLetter: true };

    return null;
  };

  save() {
    const data = this.form.value;
    const question = data as QuizQuestion;
    const correctAnswer = data.correctAnswer;
    question.correctAnswerIndex = this.indexOfLetter(correctAnswer);
    this.dialogRef.close(question);
  }

  indexOfLetter(s: string|null) {
    const letter = s?.trim().toUpperCase();
    return letter ? this.letterArray.indexOf(letter) : -1;
  }

  close() {
    this.dialogRef.close();
  }

  addAnswer(pos?: number) {
    if (this.answers.length >= this.letterArray.length) {
      return -1; // out of range
    }

    const newAnswer = this.fb.control(null, []);
    const newIndex = typeof pos === 'number' && isFinite(pos) ? pos + 1 : this.answers.length;

    this.answers.insert(newIndex, newAnswer);
    this.correctAnswer.updateValueAndValidity();

    return newIndex;
  }

  onAnswerKeyEnter(event: Event, index: number) {
    const last = index === this.answers.length - 1;

    if (last) {
      const newIndex = this.addAnswer(index);
      this.focusAnswerAt(newIndex);
    }
    else {
      const nextIndex = index + 1;
      this.focusAnswerAt(nextIndex);
    }
  }

  moveAnswerUp(index: number) {
    const answer = this.answers.at(index);
    this.answers.removeAt(index);
    this.answers.insert(index - 1, answer);
    this.correctAnswer.updateValueAndValidity();
  }

  moveAnswerDown(index: number) {
    const answer = this.answers.at(index);
    this.answers.removeAt(index);
    this.answers.insert(index + 1, answer);
    this.correctAnswer.updateValueAndValidity();
  }

  removeAnswer(index: number) {
    this.answers.removeAt(index);
    this.correctAnswer.updateValueAndValidity();
  }

  private focusAnswerAt(index: number) {
    setTimeout(() => {
      const elem = this.answerInputs.get(index);
      elem?.nativeElement?.focus();
    });
  }
}
